import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as TrackerActions from './tracker.actions';
import { SetSnackbar } from '../../store/app.actions';
import { TrackerService } from '../services/tracker.service';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  UserAssignedToTasksFromTracker,
  MultipleUsersAssignedToTasksFromTracker,
  GetFiles,
  GetProject,
  RemoveTasksOnExternalFile,
  GetBookmarksSuccess,
  LoadAllFilesBookmarksSuccess
} from 'src/app/discrepancies/store/discrepancies.actions';
import { BookmarkStatus } from 'src/app/discrepancies/models/discrepancies.model';
import { DiscrepanciesService } from 'src/app/discrepancies/services/discrepancies.service';
import { SnackBarMessage } from 'src/app/core/services/toast.service';
import { getTracker } from './tracker.selectors';
import { TAB_DISPLAY_NAMES } from '../constants/tracker.constants';

@Injectable({
  providedIn: 'root'
})
export class TrackerEffects {
  readonly actions$ = inject(Actions);
  readonly trackerService = inject(TrackerService);
  readonly discrepanciesService = inject(DiscrepanciesService);
  readonly router = inject(Router);
  readonly location = inject(Location);
  readonly store = inject(Store);

  toggleTrackerReview$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.ToggleTrackerReview),
      mergeMap(({ projectID, fileName, status, fileNames }) =>
        this.updateFileBookmarks(projectID, [fileName], status, fileNames, {
          message: status === BookmarkStatus.REVIEWED ? 'Review marked as complete' : 'Review marked as not complete'
        })
      )
    );
  });

  bulkToggleTrackerReview$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.BulkToggleTrackerReview),
      mergeMap(({ projectID, markFileNames, skippedCount, status, fileNames }) =>
        this.updateFileBookmarks(
          projectID,
          markFileNames,
          status,
          fileNames,
          this.getBulkReviewMessage(markFileNames.length, skippedCount, status)
        )
      )
    );
  });

  private updateFileBookmarks(
    projectID: string,
    markFileNames: string[],
    status: BookmarkStatus,
    fileNames: string[],
    snackbar: SnackBarMessage
  ): Observable<Action> {
    const bookmarks = markFileNames.map((fileName) => ({ fileName, status }));
    return this.discrepanciesService.createFileBookmark(projectID, bookmarks).pipe(
      switchMap((createResponse) =>
        this.discrepanciesService
          .getAllFilesBookmarks(projectID, fileNames)
          .pipe(
            switchMap((allResponse) => [
              GetBookmarksSuccess({ bookmarks: createResponse.bookmarks }),
              LoadAllFilesBookmarksSuccess({ bookmarks: allResponse.bookmarks }),
              SetSnackbar({ payload: snackbar })
            ])
          )
      ),
      catchError(() => of(SetSnackbar({ payload: { message: 'Failed to update review status' } })))
    );
  }

  private getBulkReviewMessage(markedCount: number, skippedCount: number, status: BookmarkStatus): SnackBarMessage {
    const action = status === BookmarkStatus.REVIEWED ? 'reviewed' : 'not reviewed';
    const marked = `${markedCount} ${markedCount === 1 ? 'file' : 'files'} marked as ${action}`;
    if (!skippedCount) return { message: marked, icon: 'check', color: 'var(--status-success)' };
    return {
      message: `${marked}. ${skippedCount} ${skippedCount === 1 ? 'file was' : 'files were'} skipped.`
    };
  }

  private getTabDisplayName(tabCategory: string): string {
    return TAB_DISPLAY_NAMES[tabCategory as keyof typeof TAB_DISPLAY_NAMES] || 'TLFs';
  }

  getTrackerFiles$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.GetTracker),
      switchMap(({ projectID }) =>
        this.trackerService.getTrackerFiles(projectID).pipe(
          map(({ files, columnMapping, columns }) => TrackerActions.GetTrackerSuccess({ files, columnMapping, columns })),
          catchError((err: Error) => of(TrackerActions.GetTrackerFailed({ payload: err })))
        )
      )
    );
  });

  getCachedTrackerFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TrackerActions.GetCachedTracker),
      withLatestFrom(this.store.select(getTracker)),
      switchMap(([{ projectID }, { files }]) => {
        if (files) return of(TrackerActions.GetCachedTrackerReady());
        return this.trackerService.getTrackerFiles(projectID).pipe(
          map(({ files, columnMapping, columns }) => TrackerActions.GetTrackerSuccess({ files, columnMapping, columns })),
          catchError((err: Error) => of(TrackerActions.GetTrackerFailed({ payload: err })))
        );
      })
    )
  );

  private createImportEffect(
    actionType: typeof TrackerActions.ImportTracker | typeof TrackerActions.ImportMultiWorksheetTracker,
    serviceMethod: (projectID: string, formData: FormData) => ReturnType<TrackerService['importTracker']>,
    successAction: typeof TrackerActions.ImportTrackerSuccess | typeof TrackerActions.ImportMultiWorksheetTrackerSuccess,
    failAction: typeof TrackerActions.ImportTrackerFailed | typeof TrackerActions.ImportMultiWorksheetTrackerFailed,
    errorPrefix: string
  ): Observable<Action> {
    return this.actions$.pipe(
      ofType(actionType),
      switchMap(({ projectID, formData }) =>
        serviceMethod(projectID, formData).pipe(
          switchMap(
            ({
              files,
              unmatchedUsers,
              dateColumnsWithNonDateFormat,
              userVisibilityRestricted,
              multiRoleAssignmentFiles,
              columnMapping,
              columns,
              taskIdToAssignee,
              hasMultiAssignedFiles
            }) => {
              const actions: Action[] = [
                successAction({
                  files,
                  unmatchedUsers,
                  dateColumnsWithNonDateFormat,
                  userVisibilityRestricted,
                  multiRoleAssignmentFiles,
                  columnMapping,
                  columns
                }),
                TrackerActions.ClearTrackerErrors(),
                TrackerActions.GetTracker({ projectID }),
                GetFiles({ projectID }),
                GetProject({ projectID })
              ];
              const fileName = (new Map(formData.entries())?.get('tracker') as File)?.name;

              const payload: SnackBarMessage[] = [
                { message: `${fileName} imported into the tracker.`, icon: 'check', color: 'var(--status-success)' }
              ];

              if (taskIdToAssignee) {
                actions.push(MultipleUsersAssignedToTasksFromTracker({ payload: { taskIdToAssignee } }));
                payload.push({ message: 'Tasks assigned to the production programmer.', icon: 'info' });
              }
              if (hasMultiAssignedFiles) {
                payload.push({
                  message: 'Tasks in multi-assigned files left with the previous assignment.',
                  icon: 'info',
                  color: 'var(--status-danger-light)'
                });
              }
              actions.push(SetSnackbar({ payload }));
              return actions;
            }
          ),
          catchError((err) =>
            of(
              failAction({ payload: err }),
              SetSnackbar({
                payload: { message: `${errorPrefix} failed.\n${err.error?.clientMessage || err.message}` }
              })
            )
          )
        )
      )
    );
  }

  importTracker$: Observable<Action> = createEffect(() =>
    this.createImportEffect(
      TrackerActions.ImportTracker,
      (projectID: string, formData: FormData) => this.trackerService.importTracker(projectID, formData),
      TrackerActions.ImportTrackerSuccess,
      TrackerActions.ImportTrackerFailed,
      'Import'
    )
  );

  importMultiWorksheetTracker$: Observable<Action> = createEffect(() =>
    this.createImportEffect(
      TrackerActions.ImportMultiWorksheetTracker,
      (projectID: string, formData: FormData) => this.trackerService.importMultiWorksheetTracker(projectID, formData),
      TrackerActions.ImportMultiWorksheetTrackerSuccess,
      TrackerActions.ImportMultiWorksheetTrackerFailed,
      'Multi-worksheet import'
    )
  );

  addSystemFileToTrackerTracker$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.AddSystemFileToTracker),
      switchMap(({ projectID, fileID }) =>
        this.trackerService.addSystemFileToTracker(projectID, fileID).pipe(
          map(({ file }) => TrackerActions.AddSystemFileToTrackerSuccess({ file })),
          catchError((err: Error) => of(TrackerActions.AddSystemFileToTrackerFailed({ payload: err })))
        )
      )
    );
  });

  setTrackerStatus$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.SetTrackerStatus),
      switchMap(({ projectID, trackerFileID, status }) =>
        this.trackerService.setTrackerStatus(projectID, trackerFileID, status).pipe(
          map(({ file }) => TrackerActions.SetTrackerStatusSuccess({ trackerFile: file })),
          catchError((err: Error) =>
            of(
              TrackerActions.SetTrackerStatusFailed({ err, trackerFileID }),
              SetSnackbar({ payload: { message: 'Failed to set file status' } })
            )
          )
        )
      )
    );
  });

  setTrackerCustomField$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.SetTrackerCustomField),
      switchMap(({ projectID, trackerFileID, key, value, fieldType }) => {
        return this.trackerService.setTrackerCustomField(projectID, trackerFileID, key, value, fieldType).pipe(
          map(({ file, assigned }) => TrackerActions.SetTrackerCustomFieldSuccess({ trackerFile: file, assigned })),
          catchError((err: Error) =>
            of(
              TrackerActions.SetTrackerCustomFieldFailed({ err, trackerFileID }),
              SetSnackbar({ payload: { message: `Failed to set the field "${key}"` } })
            )
          )
        );
      })
    );
  });

  bulkSetTrackerCustomField$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.BulkSetTrackerCustomField),
      switchMap(({ projectID, trackerFileIDs, updates }) => {
        return this.trackerService.bulkSetTrackerCustomField(projectID, trackerFileIDs, updates).pipe(
          switchMap(({ files, assigned, userAssignedToTasks, assignedTasks, filesAssigneeUpdate }) => {
            const actions: Action[] = [
              SetSnackbar({ payload: { message: `${files.length} Files updated` } }),
              TrackerActions.BulkSetTrackerCustomFieldSuccess({ trackerFiles: files, assigned, filesAssigneeUpdate })
            ];
            if (assignedTasks?.length) {
              actions.push(
                UserAssignedToTasksFromTracker({ payload: { assignee: userAssignedToTasks, taskIDs: assignedTasks } })
              );
            }
            return actions;
          }),
          catchError((err: Error) =>
            of(
              TrackerActions.BulkSetTrackerCustomFieldFailed({ err }),
              SetSnackbar({ payload: { message: 'Failed to update the files' } })
            )
          )
        );
      })
    );
  });

  deleteTrackerFiles$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.DeleteTrackers),
      switchMap(({ projectID, trackerFileIDs }) => {
        return this.trackerService.deleteTrackerFiles(projectID, trackerFileIDs).pipe(
          switchMap(() => [
            SetSnackbar({ payload: { message: `${trackerFileIDs.length} Files deleted` } }),
            TrackerActions.DeleteTrackersSuccess({ trackerFileIDs })
          ]),
          catchError((err: Error) =>
            of(
              TrackerActions.DeleteTrackersFailed({ err }),
              SetSnackbar({ payload: { message: 'Failed to delete the files' } })
            )
          )
        );
      })
    );
  });

  addedSystemFileToTrackerTracker$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TrackerActions.AddSystemFileToTrackerSuccess),
        tap(({ file }) => {
          const currentUrl = this.location.path();
          const relativeUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/')) + `/${file._id}`;
          this.router.navigate([relativeUrl]);
        })
      );
    },
    { dispatch: false }
  );

  assignUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.assignTrackerUser),
      map(({ projectID, users, file, userType, isCustomField }) => {
        const userIds = users.map((user) => user._id);

        return isCustomField
          ? TrackerActions.assignCustomUser({
              projectID,
              trackerFileID: file._id,
              key: userType,
              value: userIds,
              fieldType: 'user'
            })
          : TrackerActions.assignSystemUser({
              projectID,
              trackerFileID: file._id,
              userIDs: userIds,
              userType: userType as 'productionProgrammer' | 'qcProgrammer' | 'statistician',
              projectFileID: file.projectFileID
            });
      })
    );
  });

  assignSystemUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.assignSystemUser),
      switchMap((action) =>
        this.trackerService
          .assignUserToTrackerFile(
            action.projectID,
            action.trackerFileID,
            action.userIDs,
            action.userType,
            action.projectFileID
          )
          .pipe(
            switchMap(({ trackerFile, projectFile, assigned, userAssignedToTasks, assignedTasks }) => {
              const actions: Action[] = [
                TrackerActions.AssignUserToTrackerFileSuccess({ trackerFile, projectFile, assigned })
              ];
              const payload: SnackBarMessage[] = [];

              if (assignedTasks?.length) {
                payload.push({ message: 'Tasks assigned to the production programmer.', icon: 'info' });
                actions.push(
                  UserAssignedToTasksFromTracker({ payload: { assignee: userAssignedToTasks, taskIDs: assignedTasks } })
                );
              } else {
                const file = trackerFile.number ? 'TLF ' + trackerFile.number : trackerFile.name;
                if (action.userIDs.length === 0) {
                  payload.push({
                    message: `All users have been unassigned from ${file}`,
                    icon: 'check',
                    color: 'var(--status-success)'
                  });
                } else {
                  const affectedUsers = assigned.filter(({ _id }) => action.userIDs.includes(_id));
                  if (affectedUsers.length === 1) {
                    payload.push({
                      message: `${affectedUsers[0].full_name} has been assigned to ${file}`,
                      icon: 'check',
                      color: 'var(--status-success)'
                    });
                  } else {
                    payload.push({
                      message: 'Tasks in multi-assigned files left with the previous assignment.',
                      icon: 'info',
                      color: 'var(--status-danger-light)'
                    });
                  }
                }
              }
              actions.push(SetSnackbar({ payload }));
              return actions;
            }),
            catchError((err) => of(TrackerActions.AssignUserToTrackerFileFailed({ payload: err })))
          )
      )
    );
  });

  assignCustomUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.assignCustomUser),
      switchMap((action) =>
        this.trackerService
          .setTrackerCustomField(action.projectID, action.trackerFileID, action.key, action.value, action.fieldType)
          .pipe(
            switchMap(({ file, assigned }) => {
              const userIds = action.value;
              const message =
                userIds.length === 0
                  ? `All users have been unassigned from custom field "${action.key}"`
                  : `Users have been assigned to custom field "${action.key}"`;

              return [
                SetSnackbar({ payload: { message } }),
                TrackerActions.SetTrackerCustomFieldSuccess({ trackerFile: file, assigned })
              ];
            }),
            catchError((err) =>
              of(
                TrackerActions.SetTrackerCustomFieldFailed({ err, trackerFileID: action.trackerFileID }),
                SetSnackbar({ payload: { message: `Failed to set the field "${action.key}"` } })
              )
            )
          )
      )
    );
  });

  addCustomColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.AddCustomColumn),
      switchMap(({ projectID, columnName, columnType, options, position, relativeTo, tabCategory }) =>
        this.trackerService
          .addCustomColumn(projectID, columnName, columnType, options, position, relativeTo, tabCategory)
          .pipe(
            map((response) => {
              this.store.dispatch(
                SetSnackbar({
                  payload: {
                    message: `Column "${columnName}" added successfully`
                  }
                })
              );
              return TrackerActions.AddCustomColumnSuccess({
                columnName: response.columnName,
                columns: response.columns,
                files: response.files
              });
            }),
            catchError((error: Error) => {
              this.store.dispatch(
                SetSnackbar({
                  payload: {
                    message: 'Failed to add column'
                  }
                })
              );
              return of(TrackerActions.AddCustomColumnFailure({ payload: error }));
            })
          )
      )
    );
  });

  updateTrackerFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.UpdateTrackerFile),
      mergeMap((action) =>
        this.trackerService.updateTrackerFile(action.projectId, action.fileId, action.update).pipe(
          map((updatedFile) => TrackerActions.UpdateTrackerFileSuccess({ file: updatedFile })),
          catchError((error) => of(TrackerActions.UpdateTrackerFileError({ error })))
        )
      )
    );
  });

  renameColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.RenameColumn),
      switchMap(({ projectID, oldColumnName, newColumnName, tabCategory }) =>
        this.trackerService.renameColumn(projectID, oldColumnName, newColumnName, tabCategory).pipe(
          map((response) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: `Column renamed to "${newColumnName}" successfully`
                }
              })
            );
            return TrackerActions.RenameColumnSuccess({
              columnName: response.columnName,
              files: response.files,
              columns: response.columns
            });
          }),
          catchError((error: Error) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: 'Failed to rename column'
                }
              })
            );
            return of(TrackerActions.RenameColumnFailure({ payload: error }));
          })
        )
      )
    );
  });

  addRow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.AddRow),
      switchMap((params) =>
        this.trackerService.addRow(params).pipe(
          map((response) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: `${this.getTabDisplayName(params.tabCategory)} "${params.name}" added successfully`
                }
              })
            );
            return TrackerActions.AddRowSuccess({
              files: response.files
            });
          }),
          catchError((error: Error) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: `Failed to add ${this.getTabDisplayName(params.tabCategory)} row`
                }
              })
            );
            return of(TrackerActions.AddRowFailure({ payload: error }));
          })
        )
      )
    );
  });

  editColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.EditColumn),
      switchMap(({ projectID, name, columnType, options, isTypeChange, oldName, tabCategory }) =>
        this.trackerService.editColumn(projectID, name, columnType, options, isTypeChange, oldName, tabCategory).pipe(
          map((response) => {
            const message = isTypeChange
              ? `"${name}" column type changed to ${columnType} successfully`
              : `"${name}" edits saved`;
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message
                }
              })
            );
            return TrackerActions.EditColumnSuccess({
              files: response.files,
              columns: response.columns
            });
          }),
          catchError((error: Error) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: 'Failed to edit column'
                }
              })
            );
            return of(TrackerActions.EditColumnFailure({ payload: error }));
          })
        )
      )
    );
  });

  removeRow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.RemoveRow),
      switchMap(({ projectID, fileID, name }) =>
        this.trackerService.removeRow(projectID, fileID).pipe(
          mergeMap(() => {
            return [
              SetSnackbar({
                payload: {
                  message: `"${name}" file was removed`
                }
              }),
              RemoveTasksOnExternalFile({ name }),
              TrackerActions.RemoveRowSuccess({
                fileID
              })
            ];
          }),
          catchError((error: Error) => {
            return of(
              SetSnackbar({
                payload: {
                  message: `Failed to remove ${name} file`
                }
              }),
              TrackerActions.RemoveRowFailure({ payload: error })
            );
          })
        )
      )
    );
  });

  removeColumn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TrackerActions.RemoveColumn),
      switchMap(({ projectID, columnName, tabCategory }) =>
        this.trackerService.removeColumn(projectID, columnName, tabCategory).pipe(
          map((response) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: `'${columnName}' Column was removed`
                }
              })
            );
            return TrackerActions.RemoveColumnSuccess({
              columnName: response.columnName,
              files: response.files,
              columns: response.columns
            });
          }),
          catchError((error: Error) => {
            this.store.dispatch(
              SetSnackbar({
                payload: {
                  message: 'Failed to remove column'
                }
              })
            );
            return of(TrackerActions.RemoveColumnFailure({ payload: error }));
          })
        )
      )
    );
  });
}
